import React, { useState, useMemo } from 'react';
import { 
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  Download,
  Filter,
  Search,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  TrendingUp as TrendingIcon
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { DailyCashFlow } from '../../types/cash-flow';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface CashFlowTableProps {
  data: DailyCashFlow[];
  onRowClick?: (cashFlow: DailyCashFlow) => void;
  sortable?: boolean;
  filterable?: boolean;
  exportable?: boolean;
  loading?: boolean;
  title?: string;
  description?: string;
}

type SortField = 'date' | 'recebimentos' | 'pagamentos' | 'saldo_final';
type SortDirection = 'asc' | 'desc';

export function CashFlowTable({
  data,
  onRowClick,
  sortable = true,
  filterable = true,
  exportable = true,
  loading = false,
  title = "Detalhamento Diário",
  description = "Movimentação financeira detalhada por data"
}: CashFlowTableProps) {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Sort and filter data
  const processedData = useMemo(() => {
    let filtered = data;

    // Apply search filter (if needed - could filter by date range or other criteria)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = data.filter(item => {
        const dateStr = new Intl.DateTimeFormat('pt-BR').format(item.date);
        return dateStr.includes(searchLower);
      });
    }

    // Sort data
    if (sortable) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any, bValue: any;

        switch (sortField) {
          case 'date':
            aValue = a.date.getTime();
            bValue = b.date.getTime();
            break;
          case 'recebimentos':
            aValue = a.recebimentos;
            bValue = b.recebimentos;
            break;
          case 'pagamentos':
            aValue = a.pagamentos;
            bValue = b.pagamentos;
            break;
          case 'saldo_final':
            aValue = a.saldo_final;
            bValue = b.saldo_final;
            break;
          default:
            return 0;
        }

        if (sortDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    }

    return filtered;
  }, [data, sortField, sortDirection, searchTerm, sortable]);

  const handleSort = (field: SortField) => {
    if (!sortable) return;
    
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (!sortable || sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 ml-1" />
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };


  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getTrendIcon = (current: number, previous?: number) => {
    if (!previous) return <Minus className="h-3 w-3 text-muted-foreground" />;
    
    if (current > previous) {
      return <TrendingUp className="h-3 w-3 text-finance-green" />;
    } else if (current < previous) {
      return <TrendingDown className="h-3 w-3 text-finance-red" />;
    }
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  const getVariationPercentage = (current: number, previous: number) => {
    if (previous === 0) return null;
    const variation = ((current - previous) / Math.abs(previous)) * 100;
    return variation.toFixed(1) + '%';
  };

  const getSaldoBadgeVariant = (saldo: number) => {
    if (saldo > 0) return 'default';
    if (saldo < 0) return 'destructive';
    return 'secondary';
  };

  // Verificar se um dia é futuro
  const isFutureDay = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate > today;
  };

  // Verificar se um dia é hoje
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Obter classe CSS para linha baseada no tipo de dia
  const getRowClassName = (cashFlow: DailyCashFlow) => {
    const baseClass = onRowClick ? 'cursor-pointer' : '';

    // Define hover colors based on row type
    if (isToday(cashFlow.date)) {
      return `${baseClass} hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors duration-150`;
    }

    if (isFutureDay(cashFlow.date)) {
      return `${baseClass} hover:bg-amber-500/10 dark:hover:bg-amber-950/40 transition-colors duration-150`;
    }

    return `${baseClass} hover:bg-muted/50 transition-colors duration-150`;
  };

  // Obter indicador visual para valores projetados
  const getProjectedIndicator = (cashFlow: DailyCashFlow) => {
    if (!isFutureDay(cashFlow.date)) return null;
    
    return (
      <div className="flex items-center gap-1">
        <TrendingIcon className="h-3 w-3 text-amber-600" />
        <span className="text-xs text-amber-600">Projetado</span>
      </div>
    );
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Exporting cash flow data...', processedData);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            {exportable && (
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            )}
          </div>
        </div>
        
        {/* Filters */}
        {filterable && (
          <div className="flex items-center gap-4 mt-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por data..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="whitespace-nowrap">
                {processedData.length} registro(s)
              </Badge>
              
              {/* Legenda para tipos de dados */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-primary/10 dark:bg-primary/20 rounded"></div>
                  <span>Hoje</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-amber-500/10 dark:bg-amber-950/20 rounded"></div>
                  <span>Futuro</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {processedData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum dado encontrado</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              {searchTerm 
                ? 'Nenhum resultado encontrado para o termo pesquisado.'
                : 'Não há dados de fluxo de caixa para exibir.'
              }
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <ScrollArea className="h-[500px] table-scrollbar">
              <Table className="table-responsive">
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className={`${sortable ? 'cursor-pointer hover:bg-muted/50 transition-colors duration-150' : ''} min-w-[120px]`}
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">
                        Data
                        {getSortIcon('date')}
                      </div>
                    </TableHead>
                    
                    <TableHead 
                      className={`text-right ${sortable ? 'cursor-pointer hover:bg-muted/50 transition-colors duration-150' : ''} min-w-[120px]`}
                      onClick={() => handleSort('recebimentos')}
                    >
                      <div className="flex items-center justify-end">
                        Recebimentos
                        {getSortIcon('recebimentos')}
                      </div>
                    </TableHead>
                    
                    <TableHead 
                      className={`text-right ${sortable ? 'cursor-pointer hover:bg-muted/50 transition-colors duration-150' : ''} min-w-[120px]`}
                      onClick={() => handleSort('pagamentos')}
                    >
                      <div className="flex items-center justify-end">
                        Pagamentos
                        {getSortIcon('pagamentos')}
                      </div>
                    </TableHead>
                    
                    <TableHead className="text-right min-w-[120px]">
                      Transferências (entrada)
                    </TableHead>
                    
                    <TableHead className="text-right min-w-[120px]">
                      Transferências (saída)
                    </TableHead>
                    
                    <TableHead 
                      className={`text-right ${sortable ? 'cursor-pointer hover:bg-muted/50 transition-colors duration-150' : ''} min-w-[150px]`}
                      onClick={() => handleSort('saldo_final')}
                    >
                      <div className="flex items-center justify-end">
                        Saldo Final
                        {getSortIcon('saldo_final')}
                      </div>
                    </TableHead>
                    
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                
                <TableBody>
                  {processedData.map((cashFlow, index) => {
                    const previousCashFlow = index > 0 ? processedData[index - 1] : undefined;
                    const isProjected = isFutureDay(cashFlow.date);
                    
                    return (
                      <TableRow 
                        key={cashFlow.date.toISOString()}
                        className={getRowClassName(cashFlow)}
                        onClick={() => onRowClick?.(cashFlow)}
                        style={
                          isToday(cashFlow.date) 
                            ? { backgroundColor: 'hsl(var(--muted) / 0.2)' }
                            : isFutureDay(cashFlow.date)
                              ? { backgroundColor: 'hsl(var(--muted) / 0.15)' }
                              : {}
                        }
                      >
                        <TableCell className="font-medium bg-transparent">
                          <div className="flex items-center gap-2 min-w-[120px]">
                            <span className="text-sm">{formatDate(cashFlow.date)}</span>
                            <div className="flex items-center">
                              {getTrendIcon(
                                cashFlow.saldo_final, 
                                previousCashFlow?.saldo_final
                              )}
                            </div>
                            {isProjected && (
                              <TrendingIcon className="h-3 w-3 text-amber-600 flex-shrink-0" />
                            )}
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-right bg-transparent">
                          <div className="flex flex-col items-end gap-1 min-w-[120px]">
                            <span className={`font-medium text-sm ${
                              isProjected ? 'text-amber-600' : 'text-finance-green'
                            }`}>
                              {formatCurrency(cashFlow.recebimentos)}
                            </span>
                            {isProjected && (
                              <span className="text-xs text-amber-600">Projetado</span>
                            )}
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-right bg-transparent">
                          <div className="flex flex-col items-end gap-1 min-w-[120px]">
                            <span className={`font-medium text-sm ${
                              isProjected ? 'text-amber-600' : 'text-finance-red'
                            }`}>
                              {formatCurrency(cashFlow.pagamentos)}
                            </span>
                            {isProjected && (
                              <span className="text-xs text-amber-600">Projetado</span>
                            )}
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-right bg-transparent">
                          <div className="flex flex-col items-end gap-1 min-w-[120px]">
                            {cashFlow.transferencias_entrada > 0 ? (
                              <>
                                <span className={`font-medium text-sm ${
                                  isProjected ? 'text-amber-600' : 'text-finance-blue'
                                }`}>
                                  {formatCurrency(cashFlow.transferencias_entrada)}
                                </span>
                                {isProjected && (
                                  <span className="text-xs text-amber-600">Projetado</span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-right bg-transparent">
                          <div className="flex flex-col items-end gap-1 min-w-[120px]">
                            {cashFlow.transferencias_saida > 0 ? (
                              <>
                                <span className={`font-medium text-sm ${
                                  isProjected ? 'text-amber-600' : 'text-finance-amber'
                                }`}>
                                  {formatCurrency(cashFlow.transferencias_saida)}
                                </span>
                                {isProjected && (
                                  <span className="text-xs text-amber-600">Projetado</span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-right bg-transparent">
                          <div className="flex flex-col items-end gap-1 min-w-[180px]">
                            <div className="flex items-center justify-end gap-2 items-stretch w-full max-w-[200px]">
                              <Badge 
                                variant={getSaldoBadgeVariant(cashFlow.saldo_final)}
                                className="font-mono text-xs min-w-[120px] justify-center"
                              >
                                {formatCurrency(cashFlow.saldo_final)}
                              </Badge>
                              
                              <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 ml-2 min-w-[50px] text-right">
                                {previousCashFlow && getVariationPercentage(
                                  cashFlow.saldo_final, 
                                  previousCashFlow.saldo_final
                                )}
                              </span>
                            </div>
                            
                            {isProjected && cashFlow.saldo_projetado !== undefined && (
                              <div className="flex items-center gap-1 text-xs text-amber-600 self-end mt-1">
                                <TrendingIcon className="h-3 w-3 flex-shrink-0" />
                                <span>Projeção: {formatCurrency(cashFlow.saldo_projetado)}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        
                        <TableCell className="bg-transparent">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 hover:bg-muted/70 transition-colors duration-150"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => onRowClick?.(cashFlow)}>
                                <Calendar className="h-4 w-4 mr-2" />
                                Ver detalhes do dia
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Download className="h-4 w-4 mr-2" />
                                Exportar dia
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
}